import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ProgressBackdrop } from 'ui/src/components';
import { GridArea } from '../../components/common/GridArea';
import { useRecoilValue } from 'recoil';
import globalState from 'ui/src/state/globalState';
import AdminApi from '../../api/AdminApi';
import { GridOptions } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom';

const UserListGridDefs: GridOptions = {
  columnDefs: [
    {
      headerName: '성명',
      field: 'user_nm',
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => {
        const name = params.value;
        if (!name) return '';
        if (name.length <= 2) {
          return `${name[0]}*`;
        }
        return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1];
      },
    },
    {
      headerName: '아이디',
      field: 'user_id',
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: '소속',
      field: 'dept_nm',
      flex: 3,
      minWidth: 300,
      cellStyle: { textAlign: 'left' },
    },
    {
      headerName: '연락처',
      field: 'telno',
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => {
        const phone = params.value?.replace(/-/g, '');
        if (!phone) return '';
        if (phone.length === 11) {
          return `${phone.slice(0, 3)}****${phone.slice(7)}`;
        } else if (phone.length === 9) {
          return `${phone.slice(0, 2)}***${phone.slice(5)}`;
        } else if (phone.length === 10) {
          return `${phone.slice(0, 3)}***${phone.slice(6)}`;
        }
        return phone;
      },
    },
    {
      headerName: '등록일',
      field: 'reg_dt',
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: '수정일',
      field: 'mod_dt',
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: 'center' },
    },
  ],
};

const UserListPage = () => {
  const [rowData, setRowData] = useState([]);
  const gridRef = useRef<AgGridReact>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useState<any>(null);

  // 검색 필터용 상태
  const [cityList, setCityList] = useState([]);
  const [deptList, setDeptList] = useState([]);

  const { register, handleSubmit } = useForm();
  const { UserInformation } = useRecoilValue(globalState);

  const navigate = useNavigate();

  const onRowClicked = (event) => {
    const userId = event?.data?.user_id;
    if (!userId) return;
    navigate(`/main/admin/user-list/${userId}`);
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchInitialData();
    fetchData();
  }, []);

  // 초기 데이터 로드 (시/도 목록, 부서명 목록)
  const fetchInitialData = async () => {
    try {
      // 시/도 목록 조회
      const cityResult = await AdminApi.getSggSelectBox();
      setCityList(cityResult.data || []);

      // 부서명 목록 조회
      const deptResult = await AdminApi.getDeptSelectBox();
      setDeptList(deptResult.data || []);
    } catch (e) {
      console.error('초기 데이터 로드 실패:', e);
    }
  };

  useEffect(() => {
    fetchData(searchParams);
  }, [page]);

  const fetchData = async (params?: any) => {
    try {
      setLoading(true);

      // 총 개수 조회
      const countParams = {
        searchUserNm: params?.searchUserNm,
        searchUserId: params?.searchUserId,
        searchOfcNm: params?.searchOfcNm,
        searchSggNm: params?.searchCity,
      };

      const countResult = await AdminApi.getUserListCount(countParams);
      const totalCount = countResult.data || 0;
      setTotal(totalCount);

      // 사용자 목록 조회
      const listParams = {
        pageSize: 10,
        pageNo: page,
        searchUserNm: params?.searchUserNm,
        searchUserId: params?.searchUserId,
        searchOfcNm: params?.searchOfcNm,
        searchSggNm: params?.searchCity,
      };

      const listResult = await AdminApi.getUserList(listParams);
      const users = listResult.data || [];

      if (users.length < 1) {
        setRowData([]);
        return;
      }

      // API 응답 데이터를 테이블 형식에 맞게 변환
      const formattedData = users.map((user) => {
        // 소속 정보를 조합 (ctpv_nm + sgg_nm + dept_nm + ofc_nm + jbps_nm)
        const affiliation = [user.ctpv_nm, user.sgg_nm, user.dept_nm, user.ofc_nm, user.jbps_nm]
          .filter((item) => item && item.trim() !== '') // 빈 값 제외
          .join(' '); // 공백으로 구분

        return {
          user_nm: user.user_nm || '',
          user_id: user.user_id || '',
          dept_nm: affiliation,
          telno: user.telno || '',
          reg_dt: user.reg_dt_nm || (user.reg_dt ? user.reg_dt.substring(0, 10) : ''),
          mod_dt: user.mod_dt_nm || (user.mod_dt_nm ? user.mod_dt_nm.substring(0, 10) : ''),
          selected: false,
        };
      });

      console.log('Fetched user list:', formattedData);
      setRowData(formattedData);
    } catch (e) {
      console.error('API 호출 실패:', e);

      setTotal(0);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const onFirstDataRendered = (params) => {
    params.api.sizeColumnsToFit();
  };

  const onSubmit = (formData) => {
    console.log('Search params:', formData);
    setSearchParams(formData); // 검색 파라미터 저장
    setPage(0); // 검색 시 페이지를 0으로 초기화
    fetchData(formData);
  };

  return (
    <section className="cPage">
      <article className="pageTitArea">
        <div className="pageTitLeft">
          <div
            className="pageTit"
            style={{ marginTop: '16px' }}
          >
            사용자 목록 관리
          </div>
        </div>
      </article>

      <article className="pageConArea">
        <div
          className="pageSearchArea"
          style={{ display: 'flex' }}
        >
          <div className="pageSearchItem searchLabelFirst">
            <label
              className="searchLabel"
              htmlFor="searchUserNm"
            >
              성명
            </label>
            <input
              type="text"
              {...register('searchUserNm')}
              maxLength={10} // 성명 최대 길이 제한
            />
          </div>

          <div className="pageSearchItem">
            <label
              className="searchLabel"
              htmlFor="searchUserId"
            >
              아이디
            </label>
            <input
              id="searchUserId"
              type="text"
              {...register('searchUserId')}
              maxLength={30} // 아이디 최대 길이 제한
            />
          </div>

          <div className="pageSearchItem">
            <label
              className="searchLabel"
              htmlFor="searchCity"
            >
              소속
            </label>
            <select
              {...register('searchCity')}
              style={{ marginRight: '12px' }}
            >
              <option value="">시 선택</option>
              {cityList.map((city: any) => (
                <option
                  key={city}
                  value={city}
                >
                  {city}
                </option>
              ))}
            </select>
            <select
              style={{ width: '100px' }}
              {...register('searchOfcNm')}
            >
              <option value="">부서명 선택</option>
              {deptList.map((dept: any) => (
                <option
                  key={dept}
                  value={dept}
                >
                  {dept}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="bSearch searchLabelLast"
            onClick={handleSubmit(onSubmit)}
          >
            <span>검색</span>
          </button>
        </div>
      </article>

      <article className="pageConArea">
        <div className="cTableWrap mt40">
          <div className="cTableTopArea">
            <div className="cTableTopLeft">
              <ul className="cTableTopData">
                <li>
                  총 <span className="num">{total}</span> 건
                </li>
              </ul>
            </div>
          </div>
          <div className="cListTableArea2">
            <ProgressBackdrop isLoading={loading} />
            <GridArea
              gridRef={gridRef}
              columnDefs={UserListGridDefs.columnDefs}
              rowData={rowData}
              total={total}
              page={page}
              isPaging
              paginationPageSize={10}
              setPage={setPage}
              onGridReady={onFirstDataRendered}
              domLayout="autoHeight"
              defaultColDef={{
                editable: false,
                sortable: true,
                filter: true,
                resizable: true,
              }}
              onRowClicked={onRowClicked}
            />
          </div>
          <div className="cPagingArea"></div>
        </div>
      </article>
    </section>
  );
};

export default UserListPage;
